import { ReactNode } from 'react';
import { useField } from 'formik';
import {
  Checkbox,
  FormGroup,
  Typography,
  FormControl,
  FormHelperText,
  FormControlLabel,
} from '@mui/material';

import { controlError } from 'utils';
import { QuestionLabel } from 'components/question-label';

import { Icon, CheckboxOnIcon } from '../icons';

export interface CheckboxFieldProps {
  name: string;
  label?: string;
  required?: boolean;
  children?: ReactNode;
  // Provide displayValue when mapValue stores anything other than string[].
  displayValue?: string[];
  mapValue?: (values: string[]) => unknown;
  options: { label: string; value: string }[];
}

export const CheckboxField = ({
  name,
  label,
  options,
  required,
  children,
  mapValue,
  displayValue,
}: CheckboxFieldProps) => {
  const [field, meta, helpers] = useField<unknown>(name);
  const selectedValues =
    displayValue ?? (Array.isArray(field.value) ? field.value : []);
  const errorText = controlError(meta, name, label);

  return (
    <div className="w-full">
      <FormControl fullWidth required={required} error={Boolean(errorText)}>
        {label && (
          <Typography
            component="div"
            className="text-graydark-primary flex items-center text-xs leading-4.5"
          >
            <div className="min-w-0 truncate">
              <QuestionLabel value={label} />
            </div>
            {required && <span className="text-red-primary-primary"> *</span>}
          </Typography>
        )}
        <FormGroup>
          {options.map((option) => {
            const checked = selectedValues.includes(option.value);

            return (
              <FormControlLabel
                key={option.value}
                sx={{ padding: 0 }}
                label={option.label}
                control={
                  <Checkbox
                    checked={checked}
                    icon={<Icon.CheckboxOff />}
                    checkedIcon={<CheckboxOnIcon />}
                    onBlur={() => helpers.setTouched(true, true)}
                    onChange={(event) => {
                      const nextValue = event.target.checked
                        ? [...selectedValues, option.value]
                        : selectedValues.filter(
                            (value) => value !== option.value
                          );

                      helpers.setValue(
                        mapValue ? mapValue(nextValue) : nextValue,
                        true
                      );
                    }}
                  />
                }
              />
            );
          })}
        </FormGroup>

        {errorText && <FormHelperText>{errorText}</FormHelperText>}
      </FormControl>
      {children}
    </div>
  );
};
